import React from 'react';
import { TrendingUp, CalendarClock, AlertTriangle, Wallet, CheckCircle2, FileText } from 'lucide-react';

const money = (v) => (v === null || v === undefined)
  ? '—'
  : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
const pct = (v) => String(Number(v) || 0).replace('.', ',');

// Cada card também funciona como filtro rápido (onPick recebe o status alvo)
// Valores vêm 100% do summary do backend (respeita todos os filtros ativos).
export const TitulosKpiCards = ({ summary, active, onPick }) => {
  const s = summary || {};
  const cards = [
    {
      key: 'pagos', label: 'Total Recebido', value: money(s.received_total ?? s.received),
      hint: `${s.paid_count ?? 0} títulos · líquido ${money(s.received_net ?? s.received_total ?? s.received)}`,
      Icon: TrendingUp, tone: 'emerald',
      lines: [
        ['Principal recebido', money(s.received_principal)],
        ['Descontos concedidos', `- ${money(s.received_discount)}`],
        ['Multa', `+ ${money(s.received_fine)}`],
        ['Juros', `+ ${money(s.received_interest)}`],
      ],
    },
    {
      key: 'a_vencer', label: 'A Vencer', value: money(s.upcoming),
      hint: `${s.upcoming_count ?? 0} títulos`, Icon: CalendarClock, tone: 'amber',
      lines: [
        ['Nominal', money(s.upcoming_nominal)],
        ['Com desconto', money(s.upcoming)],
        ['Descontos vigentes', `- ${money(s.upcoming_discount)}`],
      ],
    },
    {
      key: 'vencido', label: 'Vencido', value: money(s.overdue),
      hint: `${s.overdue_count ?? 0} títulos`, Icon: AlertTriangle, tone: 'rose',
      lines: [
        ['Nominal', money(s.overdue_nominal)],
        ['Com desconto', money(s.overdue)],
        [`Encargos estimados (${pct(s.late_fee_percent)}% + ${pct(s.daily_interest_percent)}%/dia)`,
          `+ ${money(s.overdue_charges_estimated)}`],
      ],
    },
    {
      key: 'aberto', label: 'Em Aberto', value: money(s.open),
      hint: 'pendente + vencido + parcial', Icon: Wallet, tone: 'violet',
      lines: [
        ['Nominal', money(s.open_nominal)],
        ['Com desconto', money(s.open)],
        ['Multa + juros', `+ ${money(s.open_charges)}`],
      ],
    },
    {
      key: 'pago', label: 'Pago', value: `${s.paid_count ?? 0}`,
      hint: money(s.received_total ?? s.received), Icon: CheckCircle2, tone: 'teal',
      lines: [
        ['Principal pago', money(s.received_principal)],
        ['Desconto concedido', `- ${money(s.received_discount)}`],
        ['Multa recebida', `+ ${money(s.received_fine)}`],
        ['Juros recebidos', `+ ${money(s.received_interest)}`],
      ],
    },
    {
      key: 'all', label: 'Títulos', value: `${s.count ?? 0}`, hint: 'no filtro atual',
      Icon: FileText, tone: 'slate',
      lines: [
        ['Pagos', `${s.paid_count ?? 0}`],
        ['Parciais', `${s.partial_count ?? 0}`],
        ['Vencidos', `${s.overdue_count ?? 0}`],
      ],
    },
  ];

  const tones = {
    emerald: 'from-emerald-500/10 to-emerald-500/0 text-emerald-700 border-emerald-200',
    amber: 'from-amber-500/10 to-amber-500/0 text-amber-700 border-amber-200',
    rose: 'from-rose-500/10 to-rose-500/0 text-rose-700 border-rose-200',
    violet: 'from-violet-500/10 to-violet-500/0 text-violet-700 border-violet-200',
    teal: 'from-teal-500/10 to-teal-500/0 text-teal-700 border-teal-200',
    slate: 'from-slate-500/10 to-slate-500/0 text-slate-700 border-slate-200',
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-1 sm:grid sm:grid-cols-3 lg:grid-cols-6 sm:overflow-visible"
         data-testid="titulos-kpi-cards">
      {cards.map(({ key, label, value, hint, Icon, tone, lines }) => (
        <button
          key={key}
          type="button"
          onClick={() => onPick(key)}
          data-testid={`kpi-card-${key}`}
          title={lines.map(([l, v]) => `${l}: ${v}`).join('\n')}
          className={`min-w-[210px] sm:min-w-0 text-left rounded-2xl border bg-gradient-to-b p-4 transition-all duration-200
            hover:-translate-y-0.5 hover:shadow-lg ${tones[tone]}
            ${active === key ? 'ring-2 ring-offset-1 ring-violet-500 shadow-md' : 'shadow-sm'}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</span>
            <Icon className="w-4 h-4 opacity-70" />
          </div>
          <p className="mt-2 text-xl font-extrabold leading-none text-slate-900">{value}</p>
          <p className="mt-1 text-[11px] text-slate-500 truncate">{hint}</p>
          <div className="mt-2 space-y-0.5 border-t border-current/10 pt-1.5"
               data-testid={`kpi-card-${key}-lines`}>
            {lines.slice(0, 4).map(([l, v]) => (
              <div key={l} className="flex items-center justify-between gap-2 text-[10.5px]">
                <span className="truncate text-slate-500">{l}</span>
                <b className="whitespace-nowrap text-slate-700">{v}</b>
              </div>
            ))}
          </div>
        </button>
      ))}
    </div>
  );
};

export default TitulosKpiCards;
